import { loadTopRecordingFacets } from '../actions/facet';
import { loadRecordingsByFilter } from '../actions/recording';
import { selectActiveRecordingFacets } from '../selectors/facet';
import {
  selectFilteredAndSortedRecordings,
  selectNextFilteredRecordingsCursor,
  selectPreviousFilteredRecordingsCursor,
} from '../selectors/recording';
import { selectRecordingsFilterParams } from '../selectors/filteredList';
import { defineMessages } from 'react-intl';
import { buildCategorySchema } from '../lib/googleSchema';
import * as clientActions from '../actions/client';
import { compose } from 'redux';
import trackedViewComponent from '../hoc/trackedViewComponent';
import dataComponent from '../hoc/dataComponent';
import { connect } from 'react-redux';
import { META_TITLE_PREFIX } from '../constants';

const messages = defineMessages({
  metaDescription: {
    id: 'category-page.meta.description',
    defaultMessage:
      'Quickly find and play {categoryTitle} music from our curated repertoire, including recordings from classical masters and contemporary artists. Browse by composer, performer, instrument, genre or period.',
  },
});

const getCategoryMapStateToProps = (categorySelector, facetType, contextFacet) => {
  const ignoredFacets = [facetType];
  return function mapStateToProps(state, ownProps) {
    const { location, params } = ownProps;
    const { id } = params;
    const { query } = location;

    const category = categorySelector(state, id);
    if (!category) {
      const notFoundError = new Error('Category not found');
      notFoundError.status = 404;

      throw notFoundError;
    }

    const filterParams = selectRecordingsFilterParams(query, id, facetType);
    const activeFacets = selectActiveRecordingFacets(state, filterParams, facetType);

    const recordings = selectFilteredAndSortedRecordings(state, filterParams);

    return {
      category,
      filterParams,
      ignoredFacets,
      activeFacets,
      recordings,
      location,
      params,
      contextFacet,
      contextId: category.id.toString(),
    };
  };
};

const getCategoryFetchData = (categoryLoader, facetType, categorySelector, baseRoute) => {
  return async function fetchData(store, params, location, intl) {
    const { dispatch } = store;
    const { id, cursor } = params;
    const filterParams = selectRecordingsFilterParams(location.query, id, facetType);
    const promiseGenerators = [
      dispatch(categoryLoader(id)),
      dispatch(loadRecordingsByFilter(filterParams, cursor)),
      dispatch(loadTopRecordingFacets(filterParams)),
    ];

    await Promise.all(promiseGenerators);

    const state = store.getState();
    const category = categorySelector(state, id);
    const recordings = selectFilteredAndSortedRecordings(state, filterParams);
    const canonicalRoute = `${baseRoute}/${id}`;
    const nextCursor = selectNextFilteredRecordingsCursor(state, filterParams);
    const prevCursor = selectPreviousFilteredRecordingsCursor(state, filterParams);
    const description = intl.formatMessage(messages.metaDescription, {
      categoryTitle: category.title.toLowerCase(),
    });

    store.dispatch(
      clientActions.setHelmet({
        title: META_TITLE_PREFIX + category.title,
        description,
        canonicalRoute,
        currentCursor: params.cursor,
        nextCursor,
        prevCursor,
        googleSchema: buildCategorySchema(
          { ...category, description, pathname: canonicalRoute, recordings },
          intl
        ),
      })
    );
  };
};

const trackingProps = (state, params) => ({ id: params.id });

export default function createCategoryRoute({
  categorySelector,
  categoryLoader,
  facetType,
  contextFacet,
  baseRoute,
}) {
  const fetchData = getCategoryFetchData(categoryLoader, facetType, categorySelector, baseRoute);
  const mapStateToProps = getCategoryMapStateToProps(categorySelector, facetType, contextFacet);

  return compose(
    trackedViewComponent(contextFacet, trackingProps),
    dataComponent(fetchData),
    connect(mapStateToProps, { loadMore: loadRecordingsByFilter }, (stateProps, dispatchProps) => ({
      ...stateProps,
      ...dispatchProps,
    }))
  );
}

// Links
export function linkForNamedEntity(prefix) {
  return entity => {
    return {
      id: entity.id,
      href: `/${prefix}/${entity.id}`,
      title: entity.title,
    };
  };
}
